import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";

import { addToast } from "@/core/lib/toast";
import {
  getAccountsSummaryOptions,
  getAgbwContinueAcknowledgmentOptions,
  getAgbwProgressOptions,
  getBatchPurchaseOverviewOptions,
  getBatchSalaryOverviewOptions,
} from "@/openapi/@tanstack/react-query.gen";
import { m } from "@/paraglide/messages";

function redirectToAgbwOverview(
  clientId: string,
  showAcknowledgmentModal: boolean = false,
): never {
  if (showAcknowledgmentModal) {
    sessionStorage.setItem("showAcknowledgmentModal", "true");
  }
  throw redirect({
    to: "/auditor/clients/$id/agbw-results/overview",
    params: { id: clientId },
  });
}

export async function guardAgbwTabAccess(
  queryClient: QueryClient,
  clientId: string,
  organizationId: string,
  bookingYear: number,
): Promise<void> {
  try {
    // Always force-fetch acknowledgment to avoid stale cache from a previous run treated as valid
    const acknowledgment = await queryClient.query({
      ...getAgbwContinueAcknowledgmentOptions({
        path: {
          org_id: organizationId,
          project_id: clientId,
        },
      }),
      staleTime: 0,
    });

    // If acknowledgement exists, allow access regardless of issues
    if (
      acknowledgment?.acknowledged_at &&
      acknowledgment.acknowledged_by_email!.length > 0
    ) {
      return;
    }

    // Fetch AGBW progress to get the run number
    const progress = await queryClient.query(
      getAgbwProgressOptions({
        path: {
          org_id: organizationId,
          project_id: clientId,
        },
      }),
    );

    const run = progress?.version;
    if (!run) {
      // No run available, allow access (nothing to check)
      return;
    }

    // Fetch overview data directly using queryClient (not hooks)
    const [accountsData, batchPurchaseData, batchSalaryData] =
      await Promise.all([
        queryClient.query(
          getAccountsSummaryOptions({
            path: {
              org_id: organizationId,
              project_id: clientId,
            },
            query: {
              booking_year: bookingYear,
              run,
            },
          }),
        ),
        queryClient.query(
          getBatchPurchaseOverviewOptions({
            path: {
              org_id: organizationId,
              project_id: clientId,
            },
            query: {
              booking_year: bookingYear,
              run,
            },
          }),
        ),
        queryClient.query(
          getBatchSalaryOverviewOptions({
            path: {
              org_id: organizationId,
              project_id: clientId,
            },
            query: {
              booking_year: bookingYear,
              run,
            },
          }),
        ),
      ]);

    // Check for issues
    const unreconciledCount = accountsData?.totals?.unreconciled_count ?? 0;

    let missingBatchesExceedThreshold = false;
    if (batchPurchaseData?.rows) {
      const missingRow = batchPurchaseData.rows.find(
        (r) => r.status === "Missing",
      );
      if (missingRow && (missingRow.percentage ?? 0) > 5.0) {
        missingBatchesExceedThreshold = true;
      }
    }
    if (batchSalaryData?.rows) {
      const missingRow = batchSalaryData.rows.find(
        (r) => r.status === "Missing",
      );
      if (missingRow && (missingRow.percentage ?? 0) > 5.0) {
        missingBatchesExceedThreshold = true;
      }
    }

    // Block access only if NO acknowledgement AND there ARE issues
    if (unreconciledCount > 0 || missingBatchesExceedThreshold) {
      redirectToAgbwOverview(clientId, true);
    }

    return;
  } catch (error) {
    // On error, redirect to overview with toast
    if (error instanceof Error) {
      addToast({
        title: m.cannot_access_this_route(),
        description: error.message,
        severity: "warning",
      });
    }
    redirectToAgbwOverview(clientId, false);
  }
}
